import React, { useState } from 'react';
import { ThumbUpIcon, ThumbDownIcon } from 'features/agent-analytics/components/runCells';

/**
 * Quick-submit feedback control for a run. Posts via parent `onSubmit`.
 * Parent is responsible for POSTing to `/api/sdk/runs/:run_id/feedback`
 * (or equivalent) and refreshing the row.
 */
export default function RunFeedbackControl({ initial, onSubmit, disabled }) {
  const [satisfaction, setSatisfaction] = useState(initial?.satisfaction || null);
  const [rating, setRating] = useState(initial?.rating ?? '');
  const [comment, setComment] = useState(initial?.comment || '');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (disabled || submitting) return;
    if (!satisfaction && rating === '' && !comment) {
      setError('Add a rating, a 👍/👎, or a comment first.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        satisfaction: satisfaction || null,
        rating: rating === '' ? null : Number(rating),
        comment: comment || null,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setError(err?.message || 'Failed to submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleBtn = (active, tone) => {
    const toneVar = tone === 'positive' ? 'success' : 'error';
    return active
      ? `border-[color:var(--feedback-${toneVar})] bg-[color:var(--feedback-${toneVar}-bg)] text-[color:var(--feedback-${toneVar})]`
      : 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:border-[color:var(--border-strong)] hover:bg-[color:var(--surface-hover)]';
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-3.5"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setSatisfaction((s) => (s === 'positive' ? null : 'positive'))}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors ${toggleBtn(satisfaction === 'positive', 'positive')}`}
        >
          <ThumbUpIcon size={15} color="currentColor" filled={satisfaction === 'positive'} />
          Good
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setSatisfaction((s) => (s === 'negative' ? null : 'negative'))}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors ${toggleBtn(satisfaction === 'negative', 'negative')}`}
        >
          <ThumbDownIcon size={15} color="currentColor" filled={satisfaction === 'negative'} />
          Bad
        </button>
        <label className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-[color:var(--text-muted)]">
          Rating
          <input
            type="number"
            min="0"
            max="10"
            step="0.5"
            placeholder="—"
            value={rating}
            disabled={disabled}
            onChange={(e) => setRating(e.target.value)}
            className="w-16 rounded-lg border border-[color:var(--border-default)] bg-surface px-2 py-1 text-[12px] tabular-nums text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] outline-none focus:border-[color:var(--brand-primary)]"
          />
        </label>
      </div>
      <textarea
        value={comment}
        disabled={disabled}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Add a comment (optional)…"
        className="min-h-[64px] w-full resize-y rounded-lg border border-[color:var(--border-default)] bg-surface px-2.5 py-2 text-[12px] leading-relaxed text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] outline-none focus:border-[color:var(--brand-primary)]"
      />
      {error ? <p className="text-[11px] text-[color:var(--feedback-error)]">{error}</p> : null}
      <div className="flex items-center justify-between">
        <span className={`text-[11px] text-[color:var(--feedback-success)] transition-opacity ${saved ? 'opacity-100' : 'opacity-0'}`}>
          Feedback saved ✓
        </span>
        <button
          type="button"
          disabled={disabled || submitting}
          onClick={submit}
          className="rounded-lg bg-[color:var(--brand-primary)] px-3.5 py-1.5 text-[12px] font-semibold text-ink-onBrand transition-colors hover:bg-[color:var(--brand-primary-hover)] disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Save feedback'}
        </button>
      </div>
    </div>
  );
}
